import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store, select } from '@ngrx/store';
import { MULTI_CART_FEATURE, StateWithMultiCart } from '@spartacus/cart/base/core';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import {
  CmsProductReferencesComponent,
  GlobalMessageService,
  GlobalMessageType,
  Product,
  ProductActions,
  ProductReferenceService,
  RoutingService,
} from '@spartacus/core';
import {
  BREAKPOINT,
  BreakpointService,
  CmsComponentData,
  CurrentProductService,
  ProductReferencesComponent,
} from '@spartacus/storefront';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription, combineLatest, concat, forkJoin, of } from 'rxjs';
import {
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { ShimanoProduct } from 'src/app/model/product.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerPrefix,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import {
  PRODUCT_REFERENCES_NOT_UPSELLING,
  PRODUCT_REFERENCES_UPSELLING,
  TooltipIconService,
} from 'src/app/service/tooltip-icon/tooltip-icon.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { DealerPriceCheckerService } from 'src/app/utility/dealer-price-checker.service';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { SlickCarouselUtilService } from 'src/app/utility/slick-carousel.util';
import { POINTER_EVENTS_NONE_CLASS } from '../../tooltip-icon/tooltip-icon.component';

@Component({
  selector: 'app-shimano-product-references',
  templateUrl: './shimano-product-references.component.html',
  styleUrls: ['./shimano-product-references.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ShimanoProductReferencesComponent extends ProductReferencesComponent implements OnInit, OnDestroy {
  slickConfig: any;
  showSrp = false;
  srpSubscription$: Subscription;
  dealerPrices: DealerPrice[];
  dealerPricesGtm: DealerPrice[];
  productCodesList: string[] = [];
  dealerPrices$: Observable<any>;
  dealerPricesGtm$: Observable<any>;
  cart: any;
  code: any;
  isMobile = false;
  isTablet = false;
  csDesktopWidth = this.breakpointService.getSize(BREAKPOINT.lg);
  csTabletWidth = this.breakpointService.getSize(BREAKPOINT.sm);
  cachedProductReferences$: any = {};
  productReferenceType: string;
  isEmptyProductReferences = true;
  hasDealerPrice = true;
  dealerPriceCheckerSubscription$: Subscription;
  businessType: string;

  activeCountry$: Observable<string> = this.countryService.getActive();

  gtmbusinessType: string = this.currentBusinesstype.getBusinessType();
  affiliation = `/${this.gtmbusinessType}/${this.gtmActiveCountry}/${this.activeLanguage}`;

  noPointerEventsOnElement: string;
  tooltipIconConfigUpselling$: Observable<any> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.tooltipIconService.getTooltipIconConfig(activeCountry, PRODUCT_REFERENCES_UPSELLING)
    ),
    tap((tooltipInfoConfig) => (this.noPointerEventsOnElement = tooltipInfoConfig.noPointerEventsOnElement))
  );
  tooltipIconConfigNotUpselling$: Observable<any> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.tooltipIconService.getTooltipIconConfig(activeCountry, PRODUCT_REFERENCES_NOT_UPSELLING)
    ),
    tap((tooltipInfoConfig) => (this.noPointerEventsOnElement = tooltipInfoConfig.noPointerEventsOnElement))
  );
  showGstExclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_EXCLUSIVE)
    )
  );
  showGstInclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_INCLUSIVE)
    )
  );

  itemsUpselling$: any = {};
  itemsNonUpselling$: any = {};
  idString: string;

  constructor(
    protected cmsComponentData: CmsComponentData<any>,
    protected currentProductService: CurrentProductService,
    protected productReferencesService: ProductReferenceService,
    protected component: CmsComponentData<CmsProductReferencesComponent>,
    protected current: CurrentProductService,
    protected referenceService: ProductReferenceService,
    private deviceService: DeviceDetectorService,
    private cartService: ActiveCartFacade,
    private messageService: GlobalMessageService,
    private componentService: ComponentService,
    private dealerPriceService: DealerPriceService,
    private cdr: ChangeDetectorRef,
    private breakpointService: BreakpointService,
    private store: Store<StateWithMultiCart>,
    private gtmService: GoogleTagManagerService,
    private actionsSubject: ActionsSubject,
    private titlecasePipe: TitleCasePipe,
    private dealerPriceChecker: DealerPriceCheckerService,
    private countryService: ShimanoCountryService,
    private tooltipIconService: TooltipIconService,
    private routingService: RoutingService,
    private languageService: ShimanoLanguageService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private currentBusinesstype: CurrentBusinessType,
    private storageService: StorageService
  ) {
    super(cmsComponentData, currentProductService, productReferencesService);
    const slickConfig = new SlickCarouselUtilService();
    const slickObj = slickConfig.defaultConfig;
    slickObj.slidesToShow = 4;
    slickObj.slidesToScroll = 4;
    if (!this.deviceService.isDesktop()) {
      slickObj.arrows = false;
    }
    this.slickConfig = slickObj;
  }

  /**
   * returns an Obervable string for the title
   */
  p_title$ = super.title$;

  /**
   * returns an Obervable string for the Product Reference Type
   */
  type$ = this.component.data$.pipe(map((d) => d.productReferenceTypes));

  private currentProductCode$: Observable<string> = this.current.getProduct().pipe(
    filter(Boolean),
    map((p: Product) => p.code),
    distinctUntilChanged(),
    tap(() => {
      this.referenceService.cleanReferences();
    })
  );

  /**
   * Obervable with an Array of Observables. This is done, so that
   * the component UI could consider to lazy load the UI components when they're
   * in the viewpoint.
   */
  items$: Observable<Observable<ShimanoProduct>[]> = combineLatest([
    super.productCode$,
    this.component.data$,
    this.languageService.getActive(),
  ]).pipe(
    tap(([productCode, data]) => {
      this.productReferenceType = data.name;
      this.productReferenceService.loadProductReferences(productCode, data?.productReferenceTypes);
    }),
    switchMap(([code, data, activeLanguage]) => this.getShimanoProductReferences(code, data, activeLanguage)),
    concatMap((productReferences$: any) => {
      const productCodesList: string[] = [];
      this.dealerPrices$ = of(null);
      combineLatest([
        productReferences$.map((item: any) => {
          return concat(item);
        }),
      ]).subscribe(([products]: any) => {
        products.forEach((product: any) => {
          productCodesList.push(product.code);
        });
      });
      if (productCodesList.length) {
        this.dealerPrices$ = this.getDealerPrices(productCodesList);
      }
      return forkJoin([of(productReferences$), this.dealerPrices$]);
    }),
    switchMap(([productReferences$, dealerPricesData]) => {
      const productsList: string[] = [];
      combineLatest([
        productReferences$.map((item: any) => {
          return concat(item);
        }),
      ]).subscribe(([products]: any) => {
        products.forEach((product: any) => {
          productsList.push(product);
        });
        if (dealerPricesData && productsList.length > 0) {
          this.dealerPrices = dealerPricesData.dealerPrices;
        }
      });

      return of(productReferences$);
    })
  ) as Observable<Observable<ShimanoProduct>[]>;

  private getShimanoProductReferences(
    code: string,
    reference: any,
    language: string
  ): Observable<Observable<Product>[]> {
    const maxNumber = reference.maximumNumberProducts;
    this.idString = [code, reference.productReferenceTypes, language].join();
    if (!this.cachedProductReferences$[this.idString]) {
      this.cachedProductReferences$[this.idString] = this.referenceService
        .getProductReferences(code, reference.productReferenceTypes)
        .pipe(
          filter(Boolean),
          publishReplay(),
          refCount(),
          map((refs: any) => refs && refs.splice(0, maxNumber).map((ref: any) => of(ref.target))),
          tap((references) => {
            if (references.length) {
              switch (reference.productReferenceTypes) {
                case 'UPSELLING':
                  this.itemsUpselling$[this.idString] = references;
                  break;
                case 'CROSSELLING':
                case 'COMPATIBLE':
                  this.itemsNonUpselling$[this.idString] = references;
                  break;
              }
            }
          })
        );
    }

    return this.cachedProductReferences$[this.idString];
  }

  addToCart(code: string) {
    this.cartService
      .getEntry(code)
      .subscribe(() => {
        this.cartService.addEntry(code, 1);
        this.cart = this.store.pipe(select(MULTI_CART_FEATURE));
        const sub = this.cart.subscribe((cart: any) => {
          if (!cart.active.loading && !cart.active.error && cart.active.success) {
            this.showGlobalMessage('cart.addToCartSuccess', GlobalMessageType.MSG_TYPE_CONFIRMATION, this.code);
            sub.unsubscribe();
          }
          if (!cart.active.loading && cart.active.error && !cart.active.success) {
            sub.unsubscribe();
          }
        });
      })
      .unsubscribe();
  }
  private showGlobalMessage(text: string, param: string, messageType: GlobalMessageType) {
    this.messageService.add({ key: text, params: { productName: param } }, messageType);
    const element: HTMLElement = document.getElementsByClassName('BottomHeaderSlot')[0] as HTMLElement;
    element.scrollIntoView();
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth < this.csTabletWidth;
    this.isTablet = window.innerWidth < this.csDesktopWidth && window.innerWidth >= this.csTabletWidth;
    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.actionsSubject
      .pipe(ofType(ProductActions.LOAD_PRODUCT_REFERENCES_SUCCESS))
      .pipe(
        concatMap((productReferencesSuccess: any) => {
          const productReferences: Product[] = [];
          const productCodesList: string[] = [];
          let productReferenceType: string;
          this.dealerPricesGtm$ = of(null);
          const productReferencesList = productReferencesSuccess.payload.list;
          if (productReferencesList.length > 0) {
            productReferenceType = productReferencesList[0].referenceType;
            productReferencesList.forEach((productReference: any) => {
              productCodesList.push(productReference.target.code);
              productReferences.push(productReference.target);
            });
            this.dealerPricesGtm$ = this.getDealerPrices(productCodesList);
          }
          return forkJoin([of(productReferences), of(productReferenceType), this.dealerPricesGtm$]);
        }),
        tap(([productReferences, productReferenceType, dealerPricesData]: [Product[], string, any]) => {
          if (productReferences && dealerPricesData) {
            this.dealerPricesGtm = dealerPricesData.dealerPrices;
            this.gtmProductReferencesImpressions(productReferences, productReferenceType);
          }
        })
      )
      .subscribe();

    this.dealerPriceCheckerSubscription$ = this.componentService.hasDealerPrice$.subscribe((hasDealerPrice) => {
      this.hasDealerPrice = hasDealerPrice;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });

    this.businessType = this.currentBusinesstype.getBusinessType();
  }

  get gtmActiveCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  getDealerPrices(productCodes: string[]): Observable<any> {
    return this.dealerPriceService.getDealerPricesByProductCodes(productCodes);
  }

  dealerPrice(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode);
    }
  }

  dealerPriceValue(productCode: string, dealerPricesGtm = false): number {
    return this.dealerPriceService.getDealerPriceValueByProductCode(
      dealerPricesGtm ? this.dealerPricesGtm : this.dealerPrices,
      productCode
    );
  }

  getImageRendition(image: any, type: string) {
    let size: string;
    if (type !== 'upsell') {
      if (this.isMobile || this.isTablet) {
        size = ShimanoUtilMethod.imageRendition(177);
      } else {
        size = ShimanoUtilMethod.imageRendition(240);
      }
    } else {
      size = ShimanoUtilMethod.imageRendition(80);
    }

    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  goToPdp(product: Product, event: any, position: number) {
    if (!this.hasPointerEventsNone(event.target)) {
      this.routingService.go({
        cxRoute: 'product',
        params: product,
      });
      this.gtmProductReferencesClick(product, position);
    }
  }

  hasPointerEventsNone(element: Element): boolean {
    const closestElement = element.closest(this.noPointerEventsOnElement);
    return closestElement && closestElement.classList.contains(POINTER_EVENTS_NONE_CLASS);
  }

  gtmProductReferencesImpressions(products: any[], referenceType: string, publish?: boolean) {
    const impressionsList: any[] = [];
    products.forEach((product, index) => {
      if (product) {
        impressionsList.push({
          item_id: product.code,
          item_name: product.name,
          affiliation: this.affiliation,
          index: index,
          item_brand: product?.brand?.name,
          price: this.dealerPrice(product.code),
          item_list_name: this.titlecasePipe.transform(referenceType),
        });
      }
    });
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_IMPRESSION,
      ecommerce: {
        currency: this.storageService.getSessionStorageItem('currency'),
        items: impressionsList,
      },
    };

    const gtmSessionStorageId = GoogleTagManagerPrefix + referenceType;
    if (!this.storageService.getSessionStorageItem(gtmSessionStorageId)) {
      this.storageService.setSessionStorageItem(gtmSessionStorageId, JSON.stringify(impressionsList));
      this.gtmService.pushTag({
        ecommerce: null,
        search_term: '',
        results_page: '',
        affiliation: '',
        content_type: '',
        content_id: '',
        content_name: '',
      });
      this.gtmService.pushTag(gtmTag);
    }
  }

  gtmProductReferencesClick(product: Product, position: number) {
    this.componentService.setResetPdpQty(1);
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_CLICK,
      ecommerce: '',
      content_type: 'product',
      content_id: product.code,
      content_name: product.name,
    };

    this.gtmService.pushTag({
      ecommerce: null,
      search_term: '',
      results_page: '',
      affiliation: '',
      content_type: '',
      content_id: '',
      content_name: '',
    });
    this.gtmService.pushTag(gtmTag);
  }

  ngOnDestroy(): void {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.dealerPriceCheckerSubscription$) {
      this.dealerPriceCheckerSubscription$.unsubscribe();
    }
  }
}
